import { useEffect, useState } from "react";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import { FiSearch } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import { IoCloseSharp } from "react-icons/io5";
import { SelectInputDropdown } from "../../components/dropdown/DropDown";
import FormInput from "../../components/formInput/FormInput";
import PageName from "../../components/react_helmet/PageName";
import ReturnOrderTable from "../../components/table/ReturnOrderTable";
import { returnStatus } from "../../data";
import useReturnOrders from "./customHooks/useReturnOrders";
import useRowSelection from "./customHooks/useRowSelection";

const ReturnOrders = () => {
    const {
        page,
        setPage,
        pageSize,
        setPageSize,
        loading,
        totalPages,
        selectedOrderStatus,
        setSearchValue,
        searchValue,
        orders,
        handleDelete,
        setSelectedOrderStatus,
    } = useReturnOrders();

    const [searchHistory, setSearchHistory] = useState(() => {
        try {
            const history = localStorage.getItem("returnOrderSearchHistory");
            if (!history) return [];
            const parsed = JSON.parse(history);
            const now = Date.now();
            return parsed.filter(item => item.timestamp && (now - item.timestamp < 3600000));
        } catch (e) {
            return [];
        }
    });
    const [showHistory, setShowHistory] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setSearchHistory(prev => {
                const now = Date.now();
                const filtered = prev.filter(item => item.timestamp && (now - item.timestamp < 3600000));
                if (filtered.length !== prev.length) {
                    localStorage.setItem("returnOrderSearchHistory", JSON.stringify(filtered));
                }
                return filtered;
            });
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const clearSearch = () => {
        setSearchValue("");
        localStorage.removeItem('returnOrderSearch');
        setPage(1);
    };

    const saveToHistory = (term) => {
        const cleanTerm = term?.trim();
        if (!cleanTerm || cleanTerm.length < 2) return;
        setSearchHistory((prev) => {
            const currentHistory = Array.isArray(prev) ? prev : [];
            const now = Date.now();
            const newHistory = [
                { term: cleanTerm, timestamp: now },
                ...currentHistory.filter((item) => item.term !== cleanTerm)
            ].slice(0, 10);
            localStorage.setItem("returnOrderSearchHistory", JSON.stringify(newHistory));
            return newHistory;
        });
    };

    useEffect(() => {
        if (searchValue && searchValue.trim().length >= 3) {
            const timer = setTimeout(() => {
                saveToHistory(searchValue);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [searchValue]);

    const handleHistoryClick = (term) => {
        setSearchValue(term);
        setPage(1);
        localStorage.setItem('returnOrderSearch', term);
        setShowHistory(false);
        saveToHistory(term);
    };

    const clearHistory = (e) => {
        e.stopPropagation();
        setSearchHistory([]);
        localStorage.removeItem("returnOrderSearchHistory");
    };

    return (
        <div className="settings-container">
            <PageName title={"Return Orders"} />
            <div className="px-4">
                {/* Combined Header & Filter Section */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    {/* Left: Title */}
                    <div className="d-flex align-items-center gap-3">

                        <div>
                            <h4 className="fw-bold text-dark mb-0" style={{ fontSize: '24px' }}>Return Orders</h4>
                            <p className="text-muted small mb-0">({totalPages ?? 0} Total)</p>
                        </div>
                    </div>

                    {/* Center: Search Bar */}
                    <div className="flex-1 d-flex justify-content-center px-4">
                        <div className="position-relative" style={{ width: "400px" }}>
                            <FormInput
                                type={"text"}
                                placeholder={"Search order ID, status or SKU..."}
                                className="border-1 shadow-none"
                                size="large"
                                icon={<FiSearch size={"20"} className="text-muted" />}
                                handleChange={(e) => {
                                    setSearchValue(e.target.value);
                                    setPage(1)
                                    localStorage.setItem('returnOrderSearch', e.target.value)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        saveToHistory(searchValue);
                                        setShowHistory(false);
                                    }
                                }}
                                onFocus={() => setShowHistory(true)}
                                onBlur={() => setTimeout(() => setShowHistory(false), 200)}
                                value={searchValue}
                                suffix={
                                    searchValue ? (
                                        <IoCloseSharp
                                            size={18}
                                            className="text-muted pointer"
                                            onClick={clearSearch}
                                        />
                                    ) : null
                                }
                            />
                            {showHistory && searchHistory.length > 0 && (
                                <div 
                                    className="position-absolute w-100 bg-white border shadow-lg mt-1" 
                                    style={{ 
                                        zIndex: 2000, 
                                        borderRadius: '4px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        top: '100%',
                                        left: 0
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light sticky-top">
                                        <span className="text-muted small fw-bold">RECENT SEARCHES</span>
                                        <span 
                                            className="text-primary small cursor-pointer fw-bold" 
                                            onClick={clearHistory}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Clear All
                                        </span>
                                    </div>
                                    {searchHistory.map((item, index) => (
                                        <div
                                            key={index}
                                            className="d-flex align-items-center p-2 border-bottom"
                                            style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onClick={() => handleHistoryClick(item.term)}
                                        >
                                            <BiSearch size={16} className="text-muted me-2" />
                                            <span className="flex-grow-1" style={{ fontSize: '14px', color: '#333' }}>{item.term}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Status Filter */}
                    <div className="d-flex align-items-center">
                        <div style={{ width: "300px" }}>
                            <SelectInputDropdown
                                type={"select"}
                                placeholder={"All Return Orders"}
                                options={returnStatus}
                                className={"saleOrderDropdown border-1 shadow-none h-100"}
                                value={selectedOrderStatus}
                                handleChange={(e) => {
                                    setSelectedOrderStatus(e.target.value);
                                    localStorage.setItem("selectedReturnOrderStatus", e.target.value);
                                    localStorage.setItem("returnOrderPage", 1);
                                }}
                            />
                        </div>
                    </div>
                </div>


                <div className="settings-card">
                    {loading ? (
                        <TableSkeleton columns={10} />
                    ) : (
                        <ReturnOrderTable
                            searchValue={searchValue}
                            data={orders}
                            handleDelete={handleDelete}
                            page={page}
                            totalPages={totalPages}
                            setPage={setPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            onClearSearch={searchValue ? () => {
                                setSearchValue("");
                                setPage(1);
                            } : undefined}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReturnOrders;
